import React from 'react';
import { compose, withHandlers, withProps, lifecycle, withState } from 'recompose';
import { Grid as Grid2, Table, TableHeaderRow } from '@devexpress/dx-react-grid-material-ui';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import withStyles from '@material-ui/core/styles/withStyles';
import Grid from '@material-ui/core/Grid';
import { get, find, reject } from 'lodash';
import {
  getCategory,
  addAttributeToCategory,
  removeAttributeToCategory,
  updateCategory,
} from './_api';
import { attributes } from '../attributes/_api';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import withLoader from '../app/withLoader';
import EditForm from './form/addForm';
import { withSnackbar } from 'notistack';
import FlagDialog from './flagDialog';
import { attributeLevel } from '../../utils/constants/';

const styles = (theme) => ({
  root: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    padding: 20,
  },
});

const enhance = compose(
  withStyles(styles),
  withLoader,
  withProps(({ match: { params: { id } }, addAttribute }) => ({
    categoryId: id,
  })),
  withSnackbar,
  withState('item', 'setItem', null),
  withState('edit', 'setEdit', false),
  withState('flagDialog', 'setFlagDialog', false),
  withHandlers({
    loadItem: ({ categoryId, setItem }) => async () => {
      const resp = await getCategory(categoryId);
      if (resp) {
        setItem(resp);
      }
    },
  }),
  withHandlers({
    addAttribute: ({ categoryId, toggleLoader, loadItem }) => async (id, flags) => {
      toggleLoader();
      await addAttributeToCategory(categoryId, id, flags);
      await loadItem();
      toggleLoader(false);
    },
    removeAttribute: ({ categoryId, toggleLoader, loadItem }) => async (id) => {
      toggleLoader();
      await removeAttributeToCategory(categoryId, id);
      await loadItem();
      toggleLoader(false);
    },
    onSubmit: ({ toggleLoader, categoryId, loadItem, setEdit, enqueueSnackbar }) => async (
      values
    ) => {
      toggleLoader();
      const resp = await updateCategory(categoryId, values);
      if (get(resp, 'success', false)) {
        enqueueSnackbar('Kategorie byla úspěšně změněna.', { variant: 'success' });
      } else {
        enqueueSnackbar('Kategorie se nepovedlo upravit.', { variant: 'error' });
      }
      await loadItem();
      toggleLoader(false);
      setEdit(false);
    },
  }),
  withProps(({ removeAttribute, setFlagDialog }) => {
    return {
      columns: [
        { name: 'name', title: 'Jméno' },
        { name: 'description', title: 'Popis' },
        {
          name: 'status',
          title: 'Stav',
          getCellValue: (row) => get(find(attributeLevel, { id: get(row, 'level', '') }), 'label'),
        },
        {
          name: 'add',
          title: 'Přidat',
          getCellValue: (row) => (
            <Button
              variant="fab"
              mini
              color="primary"
              aria-label="Add"
              onClick={() => {
                setFlagDialog({ open: true, id: row.id });
              }}
            >
              <AddIcon />
            </Button>
          ),
        },
      ],
      columns2: [
        { name: 'name', title: 'Jméno' },
        { name: 'description', title: 'Popis' },
        {
          name: 'status',
          title: 'Stav',
          getCellValue: (row) => get(find(attributeLevel, { id: get(row, 'level', '') }), 'label'),
        },
        {
          name: 'remove',
          title: 'Odebrat',
          getCellValue: (row) => (
            <Button
              variant="fab"
              mini
              color="primary"
              aria-label="Delete"
              onClick={() => removeAttribute(row.id)}
            >
              <DeleteIcon />
            </Button>
          ),
        },
      ],
    };
  }),
  withState('attributes', 'setAttributes', []),
  lifecycle({
    async componentDidMount() {
      const { categoryId, setItem, setAttributes, toggleLoader } = this.props;
      toggleLoader();
      const resp = await getCategory(categoryId);
      if (resp) {
        setItem(resp);
      }
      const atts = await attributes();
      if (atts) {
        setAttributes(atts.items);
      }
      toggleLoader(false);
    },
  }),
  withProps(({ item, attributes }) => {
    const atts = reject(attributes, (item2) => {
      const att = find(get(item, 'attributes', []), { id: item2.id });
      return att ? true : false;
    });
    return { attributesToShow: atts };
  })
);

export default enhance(
  ({
    classes,
    categoryId,
    item,
    attributesToShow,
    columns,
    columns2,
    edit,
    setEdit,
    onSubmit,
    flagDialog,
    setFlagDialog,
    addAttribute,
  }) => (
    <div className={classes.root}>
      <Grid container spacing={8}>
        {!edit && (
          <React.Fragment>
            <Grid item xs={12}>
              <b>Jméno:</b> {get(item, 'name', '')}
            </Grid>
            <Grid item xs={12}>
              <b>Popis:</b> {get(item, 'description', '')}
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" color="primary" onClick={() => setEdit(true)}>
                Upravit
              </Button>
            </Grid>
          </React.Fragment>
        )}
        {edit && (
          <Grid item xs={12}>
            <EditForm
              onSubmit={onSubmit}
              edit={edit}
              setEdit={setEdit}
              initialValues={{
                name: get(item, 'name', ''),
                description: get(item, 'description', ''),
                unifiedItems: get(item, 'unifiedItems', false),
              }}
            />
          </Grid>
        )}
        <Grid item xs={6} style={{ marginTop: 20 }}>
          <Paper>
            <h3 style={{ padding: 10 }}>Aktivní atributy</h3>
            <div className="grid--clickable grid--custom">
              <Grid2 rows={get(item, 'attributes', [])} columns={columns2}>
                <Table />
                <TableHeaderRow />
              </Grid2>
            </div>
          </Paper>
        </Grid>
        <Grid item xs={6} style={{ marginTop: 20 }}>
          <Paper>
            <h3 style={{ padding: 10 }}>Neaktivní atributy</h3>
            <div className="grid--clickable grid--custom">
              <Grid2 rows={attributesToShow} columns={columns}>
                <Table />
                <TableHeaderRow />
              </Grid2>
            </div>
          </Paper>
        </Grid>
      </Grid>
      {flagDialog.open && (
        <FlagDialog
          flagDialog={flagDialog}
          setFlagDialog={setFlagDialog}
          addAttribute={addAttribute}
        />
      )}
    </div>
  )
);



// WEBPACK FOOTER //
// ./src/modules/categories/detail.js